/**
 * @file unitPriceLabel.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description What a unit price or unit cost is PER, said beside it — ONE rule
 *              for every screen and document, Pricing & Costing included.
 *
 *   Cam, 28 Oct 2026 (Pricing & Costing redesign, decision 7): "so long as the
 *   user is totally clear what £/ unit UNITS actually are (i.e. whether
 *   £100/T or /Tote or /25kg Bag etc.) when they are looking either at the SO,
 *   PO, Bill, Invoice … What we CANNOT have come the first few days of
 *   November, when sending out invoices and statements to customers, is unit
 *   sale prices being mixed up and therefore customers being over/ under
 *   charged."
 *
 *   A LINE'S PRICE IS PER THE LINE'S UNIT — its own unit weight (0 = None),
 *   else its item's, else a tonne on a bulk sales code (utils/lineUnits,
 *   utils/qtyWithTonnes). So:
 *     a tonne              "/T"             "per tonne"
 *     a pack               "/25kg bag"      "per 25kg bag"    (and "/500kg tote", "/15.873kg")
 *     milk powder, no unit "/bag"           "per bag"         (its price is per bag — utils/itemCostUnit)
 *     None (no weight)     " each"          "each"            (a count: a service or a charge)
 *     a bill line per kg   "/kg"            "per kg"
 *     unknown              ""               "unit not set"    — never a guessed unit; screens SAY it
 *   A pack price also reads per tonne (`perTonne`: £10.00 a 25kg bag = £400.00
 *   a tonne), which is how a tonne's price typed on a tote line shows itself.
 *
 *   The facts are qtyWithTonnes.qtyFactsOf's (a line's own weight, its item's,
 *   its sales account, a bill line's printed unit); `priceFactsOf` reads them
 *   from a row in either naming. Display only — nothing here changes a figure.
 *
 *   Two ways of writing it, both from perUnitOf:
 *     suffix  right after a figure: "/T", "/25kg bag", "/bag", " each", "/kg",
 *             " (unit not set)"  — "£400.00/T", "£5.00 each"
 *     phrase  after a space, in a sentence or on a document: "per tonne",
 *             "per 25kg bag", "per bag", "each", "per kg", "(unit not set)"
 *
 *   1.1.0 (2026-10-28, N6 — Cam's D7 note; two streams had built a helper each):
 *   Pricing & Costing's perUnitLabel (utils/pricePer, which now re-exports it
 *   from here), the rep phone line blocks' perUnitText (utils/repOrderCard) and
 *   the reps' pricing screen's unitWordsOf (utils/repPricingCards) all read this
 *   rule. One wording for an unknown unit: "unit not set" (P&C said "/unit?").
 *   priceFactsOf also reads a Pricing & Costing / rep row (unit_kg — the line's
 *   unit weight as the server worked it out — and item.range, its sales code);
 *   perUnitLabel(row) is what P&C shows; perUnitOfLabel(label) reads a unit
 *   label a server sent ("T", "25kg bag", "none", or a row's "/T", " each").
 *   The server's pricingCostingService.unitOf is this rule too, so a bulk
 *   product with no weight (Harvester 18, sales codes 201/202/204/206/209)
 *   reads per tonne there as everywhere.
 *
 *   The server's utils/unitPriceLabel.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { isNoWeight, lineWeightKg } from './lineUnits';
import { unitLabelFor } from './units';

/** Sales codes counted in tonnes when the item has no weight (utils/tonnage). */
const BULK_CODES = ['201', '202', '204', '206', '209'];
/** Milk powder: priced per bag. */
const MILK_POWDER = '200P';

/** What a screen says when nothing gives the unit. */
export const UNIT_NOT_SET = 'unit not set';

const blank = (v) => v === '' || v === null || v === undefined || String(v).trim() === '';
const num = (v) => (blank(v) ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
const codeOf = (v) => String(v ?? '').trim().toUpperCase();
const r4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;

/** A bill line's printed unit, loosely: 'kg' | 't' | 'pack' | null. */
function printedUnit(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'kg' || s === 'kgs' || s === 'kilo' || s === 'kilos' || s === 'kilogram' || s === 'kilograms') return 'kg';
  if (s === 't' || s === 'tonne' || s === 'tonnes' || s === 'ton' || s === 'tons' || s === 'mt') return 't';
  if (s === 'pack') return 'pack';
  return null;
}

/**
 * The facts from a row in either naming (snake_case or camelCase), plus a
 * line's item where the row carries it as `item` / `item_unit_weight_kg`.
 * A Pricing & Costing or rep pricing row (1.1.0) gives its unit as `unit_kg`
 * (the line's own, else its item's, else a tonne on a bulk code; 0 = None —
 * the server worked it out) and its sales code as `item.range` /
 * `item_sales_account_code`.
 */
export function priceFactsOf(line = {}) {
  const pick = (...keys) => {
    for (const k of keys) if (line?.[k] !== undefined && line?.[k] !== null && line?.[k] !== '') return line[k];
    return null;
  };
  const item = line?.item && typeof line.item === 'object' ? line.item : null;
  const range = item && !blank(item.range) ? item.range : null;
  return {
    unitWeightKg: pick('unit_weight_kg', 'unitWeightKg', 'unit_kg'),
    itemUnitWeightKg: pick('item_unit_weight_kg', 'itemUnitWeightKg'),
    accountCode: pick('item_sales_account_code', 'itemSalesAccountCode', 'sales_account_code', 'salesAccountCode', 'account_code', 'accountCode') ?? range,
    unit: pick('qty_unit', 'qtyUnit', 'unit'),
  };
}

/**
 * What one unit of the line is, for its price.
 * @param {object} f
 * @param {number|string|null} [f.unitWeightKg]      the line's own (0 = None, blank = the item's)
 * @param {number|string|null} [f.itemUnitWeightKg]  the item's
 * @param {string|null}        [f.accountCode]       the item's (or a sales line's) account — bulk codes count in tonnes, 200P per bag
 * @param {string|null}        [f.unit]              a bill line's printed unit ('kg' | 't' | 'pack')
 * @returns {{ kind: 'tonne'|'pack'|'bag'|'none'|'kg'|'unknown', kg: number|null,
 *   per: string, words: string, short: string, title: string }}
 *   per: "/T", "/25kg bag", "/bag", " each", "/kg" or '' (unknown);
 *   words: "per tonne", "per 25kg bag", "per bag", "each", "per kg", "unit not set";
 *   short: "/T", "/25kg bag", "/bag", "each", "/kg", "unit not set" — for a narrow cell;
 *   suffix: per, or " (unit not set)" — right after a figure (1.1.0);
 *   phrase: words, or "(unit not set)" — after a space (1.1.0).
 */
export function perUnitOf(f = {}) {
  const printed = printedUnit(f.unit);
  if (printed === 't') return out('tonne', 1000);
  if (printed === 'kg') return out('kg', 1);
  if (isNoWeight(f.unitWeightKg)) return out('none', 0);
  const kg = lineWeightKg(f.unitWeightKg, f.itemUnitWeightKg);
  if (kg === 1000) return out('tonne', 1000);
  if (kg) return out('pack', kg);
  const code = codeOf(f.accountCode);
  if (code === MILK_POWDER) return out('bag', null);
  if (BULK_CODES.includes(code)) return out('tonne', 1000);
  return out('unknown', null);
}

function out(kind, kg, packLabel = null) {
  switch (kind) {
    case 'tonne': return { kind, kg, per: '/T', words: 'per tonne', short: '/T', suffix: '/T', phrase: 'per tonne', title: 'Per tonne' };
    case 'kg': return { kind, kg, per: '/kg', words: 'per kg', short: '/kg', suffix: '/kg', phrase: 'per kg', title: 'Per kg (as billed)' };
    case 'none': return { kind, kg: 0, per: ' each', words: 'each', short: 'each', suffix: ' each', phrase: 'each', title: 'Each — None, no weight: a count' };
    case 'bag': return { kind, kg, per: '/bag', words: 'per bag', short: '/bag', suffix: '/bag', phrase: 'per bag', title: 'Per bag (milk powder)' };
    case 'pack': {
      const label = packLabel || unitLabelFor(kg);
      return { kind, kg, per: `/${label}`, words: `per ${label}`, short: `/${label}`, suffix: `/${label}`, phrase: `per ${label}`, title: `Per ${label}` };
    }
    default:
      return {
        kind: 'unknown', kg: null, per: '', words: UNIT_NOT_SET, short: UNIT_NOT_SET,
        suffix: ` (${UNIT_NOT_SET})`, phrase: `(${UNIT_NOT_SET})`,
        title: 'No unit on this line or its item — nobody can tell what this price is per. Set the line\'s unit.'
      };
  }
}

/**
 * A unit label a server sent, read by the same rule (1.1.0): "T" / "tonne",
 * "25kg bag", "500kg tote", "15.873kg", "bag" (milk powder), "none" / "each"
 * (None), "kg" — or a row's own `per` ("/T", " each"). Blank = unknown.
 */
export function perUnitOfLabel(label) {
  const s = String(label ?? '').trim().replace(/^\//, '').trim();
  if (!s) return out('unknown', null);
  const low = s.toLowerCase();
  if (low === 't' || low === 'tonne' || low === 'tonnes') return out('tonne', 1000);
  if (low === 'none' || low === 'each') return out('none', 0);
  if (low === 'bag' || low === 'bags') return out('bag', null);
  if (low === 'kg') return out('kg', 1);
  const m = /^(\d+(?:\.\d+)?)\s*kg\b/i.exec(s);
  return out('pack', m ? Number(m[1]) : null, s);
}

/**
 * What a Pricing & Costing (or rep pricing) row's figures are per (1.1.0 —
 * was utils/pricePer.perUnitLabel, which re-exports this): the row's facts by
 * perUnitOf; a row that does not carry them (a till line) by the `per` the
 * server worked out with this same rule, else its `unit` label ("25kg bag").
 * @returns {{ text: string, title: string, known: boolean, kind: string, phrase: string }}
 *   text: the suffix — "/T", "/25kg bag", "/bag", " each", " (unit not set)".
 */
export function perUnitLabel(row) {
  let p = perUnitOf(priceFactsOf(row || {}));
  if (p.kind === 'unknown' && !blank(row?.per)) p = perUnitOfLabel(row.per);
  if (p.kind === 'unknown' && !blank(row?.unit)) p = perUnitOfLabel(row.unit);
  return { text: p.suffix, title: p.title, known: p.kind !== 'unknown', kind: p.kind, phrase: p.phrase };
}

/**
 * A money figure with what it is per: "£400.00/T", "£10.00/25kg bag",
 * "£5.00 each", "£12.00 (unit not set)". `moneyText` is the caller's own
 * formatted figure (formatPrice on screens, the PDF's helper on documents);
 * blank in, blank out. `unknown`: what an unknown unit adds ('' on a
 * customer's document, where nothing is better than an internal note).
 */
export function withPerUnit(moneyText, f = {}, { unknown = UNIT_NOT_SET } = {}) {
  if (blank(moneyText)) return '';
  const p = perUnitOf(f);
  if (p.kind === 'unknown') return unknown ? `${moneyText} (${unknown})` : String(moneyText);
  return `${moneyText}${p.suffix}`;
}

/**
 * The same price per tonne, for a pack line (£10.00 a 25kg bag = £400.00 a
 * tonne) — null for a tonne, None, bag, kg or unknown line, or no figure.
 */
export function perTonne(value, f = {}) {
  const v = num(value);
  if (v === null) return null;
  const p = perUnitOf(f);
  if (p.kind !== 'pack' || !(p.kg > 0)) return null;
  return r4((v * 1000) / p.kg);
}

/**
 * The supplied quantity's state, for a line that is invoiced on it (Cam, 28
 * Oct 2026: "Make clear … supplied qty … qty if no supplied qty entered — but
 * they need to provide one before invoicing — make clear whether supplied qty
 * shown or if needed"). Recorded: { recorded: true, text: '' } (the caller
 * shows the figure with its unit); not: the words to show in its place.
 */
export function suppliedState(supplied, { invoiced = false } = {}) {
  if (!blank(supplied)) return { recorded: true, text: '', title: 'Supplied — what is invoiced' };
  return invoiced
    ? { recorded: false, text: 'Not recorded', title: 'No supplied quantity was recorded — the quantity ordered was invoiced' }
    : {
      recorded: false,
      text: 'Not recorded yet — needed before invoicing',
      title: 'No supplied quantity yet: until one is recorded the order is invoiced at the quantity ordered. Record what went before invoicing.'
    };
}
